// Shared helpers for stop payments: status → badge, what a stop covers,
// and the client-side expiry check.

import { StopPaymentReturn } from '../clients/rust-api-client';
import { formatCurrency, formatDate } from './bankHelpers';

export type BadgeColor = 'lime' | 'red' | 'amber' | 'zinc' | 'sky' | 'purple';

/** The most of the reason the bank keeps. It upper-cases what it keeps. */
export const BANK_REASON_MAX_LEN = 20;

/**
 * Today as a bare `YYYY-MM-DD` in the browser's zone. A stop's expiry is
 * a calendar date from the bank, so the comparison is on calendar days,
 * never on instants.
 */
export const todayIso = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/**
 * The status a stop reads as. The server already derives `expired` for
 * an active stop past its date, but a list fetched yesterday and left
 * on screen should not say "Active" past midnight either.
 */
export const effectiveStatus = (s: StopPaymentReturn): string => {
  if (s.status === 'active' && s.expiration_date && s.expiration_date < todayIso()) {
    return 'expired';
  }
  return s.status;
};

export const statusColor = (status: string): BadgeColor => {
  switch (status) {
    case 'active':
      return 'lime';
    case 'failed':
      return 'red';
    case 'pending':
    case 'placing':
    case 'releasing':
      return 'sky';
    case 'released':
    case 'expired':
    default:
      return 'zinc';
  }
};

export const statusLabel = (s: StopPaymentReturn): string => {
  switch (effectiveStatus(s)) {
    case 'pending':
    case 'placing':
      return 'Placing…';
    case 'active':
      return s.expiration_date
        ? `Active until ${formatDate(s.expiration_date)}`
        : 'Active';
    case 'failed':
      return 'Failed';
    case 'releasing':
      return 'Releasing…';
    case 'released':
      return 'Released';
    case 'expired':
      return 'Expired';
    default:
      return s.status;
  }
};

/** A stop is still in force, or on its way to being: the bank may hold it. */
export const isOpen = (s: StopPaymentReturn): boolean =>
  ['pending', 'placing', 'active', 'releasing'].includes(effectiveStatus(s));

export const canPlaceAgain = (s: StopPaymentReturn): boolean =>
  s.status === 'failed';

/** Active stops are released at the bank; failed ones are just removed. */
export const canRelease = (s: StopPaymentReturn): boolean =>
  ['active', 'failed'].includes(effectiveStatus(s));

/** `#1234 · 10/12/2026 · $125.00` or `#1445–1450`. */
export const describeKey = (s: StopPaymentReturn): string => {
  if (s.kind === 'range') {
    return `#${s.first_check_number ?? '?'}–${s.last_check_number ?? '?'}`;
  }
  const parts = [`#${s.check_number ?? '?'}`];
  if (s.check_date) parts.push(formatDate(s.check_date));
  if (s.amount) parts.push(formatCurrency(Number(s.amount)));
  return parts.join(' · ');
};

/**
 * When the bank last confirmed this stop — at placement, or at the last
 * refresh that found it listed. What "Active" is true as of; the bank
 * cannot tell us about a change in between.
 */
export const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime())
    ? '-'
    : d.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

/** How many checks a range covers, for the dialog's live count. */
export const rangeCount = (first: string, last: string): number | null => {
  if (!/^\d+$/.test(first.trim()) || !/^\d+$/.test(last.trim())) return null;
  const f = Number(first);
  const l = Number(last);
  if (!(f < l)) return null;
  return l - f + 1;
};
